import {
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  ArrowRight,
  Globe,
  CheckCircle,
  XCircle,
  FileText
} from 'lucide-react'
import { Link } from 'react-router-dom'

interface CompetitorScore {
  id: string
  name: string
  domain: string
  aiIndexScore: number
  mentionRate: number
  trend: 'up' | 'down' | 'stable'
  avgPosition: number | null
  hasLlms?: boolean
  hasManifest?: boolean
  hasWebhooks?: boolean
  scoreChange?: number
  queriesAnalyzed?: number
  shareOfVoice?: number
  sentimentScore?: number
  favorable?: number
  neutral?: number
  unfavorable?: number
}

interface TopCompetitorsProps {
  competitors: CompetitorScore[]
  loading?: boolean
  limit?: number
}

const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'stable' }) => {
  if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />
  if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />
  return <Minus className="h-4 w-4 text-gray-400" />
}

const FeatureBadge = ({
  enabled,
  icon: Icon,
  label
}: {
  enabled: boolean
  icon: any
  label: string
}) => (
  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
    enabled
      ? 'bg-green-100 text-green-700'
      : 'bg-gray-100 text-gray-500'
  }`}>
    {enabled ? (
      <CheckCircle className="h-3 w-3" />
    ) : (
      <XCircle className="h-3 w-3" />
    )}
    <Icon className="h-3 w-3" />
    <span>{label}</span>
  </div>
)

export default function TopCompetitors({ competitors, loading, limit = 3 }: TopCompetitorsProps) {
  // Only show head-to-head competitors (max 3)
  const topCompetitors = competitors.slice(0, limit)

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    )
  }

  if (topCompetitors.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Target className="h-5 w-5 mr-2 text-indigo-600" />
            Head-to-Head Competitors
          </h2>
        </div>
        <div className="text-center py-12">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No head-to-head competitors selected yet</p>
          <p className="text-sm text-gray-500 mt-2">Use the star icon on the Competition page to add up to 3 competitors</p>
          <Link
            to="/competition"
            className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-700"
          >
            Go to Competition page
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Target className="h-5 w-5 mr-2 text-indigo-600" />
            Head-to-Head Competitors
          </h2>
          <Link
            to="/competition"
            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
          >
            Manage
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Competitor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Index Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mention Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Integration
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {topCompetitors.map((competitor) => (
              <tr
                key={competitor.id}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{competitor.name}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Globe className="h-3 w-3 mr-1" />
                      {competitor.domain}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-indigo-600">
                      {competitor.aiIndexScore.toFixed(1)}
                    </span>
                    <div className="flex items-center space-x-1">
                      <TrendIcon trend={competitor.trend} />
                      {competitor.scoreChange !== undefined && competitor.scoreChange !== 0 && (
                        <span className={`text-xs font-medium ${
                          competitor.trend === 'up' ? 'text-green-600' :
                          competitor.trend === 'down' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {competitor.scoreChange > 0 ? '+' : ''}{competitor.scoreChange}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold text-gray-900">{competitor.mentionRate.toFixed(1)}%</span>
                    </div>
                    {competitor.queriesAnalyzed !== undefined && (
                      <div className="text-xs text-gray-500">
                        in {competitor.queriesAnalyzed} queries
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    <FeatureBadge enabled={competitor.hasLlms || false} icon={FileText} label="LLMS" />
                    <FeatureBadge enabled={competitor.hasManifest || false} icon={Globe} label="Manifest" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <Link
          to="/competition"
          className="block text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          View detailed competitor analysis →
        </Link>
      </div>
    </div>
  )
}
